import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Output,
    ViewChild,
    ViewEncapsulation,
    OnDestroy,
    inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '@environments/environment';
import {
    KeplerCoveoService,
    KeplerCoveoSuggestion,
    KeplerCoveoSuggestionsResponse,
    KeplerSfdcService,
    ProductService,
    UserService,
} from '@services';
import { SupportCase } from '@types';
import {
    BehaviorSubject,
    catchError,
    combineLatestWith,
    firstValueFrom,
    iif,
    Observable,
    of,
    switchMap,
    tap,
    timer,
    Subscription,
    filter,
} from 'rxjs';
import { GknRoutePath } from 'src/app/app-routing.enum';
import { RecentlyViewedService } from 'src/app/search-results/headless-search/services/recently-viewed.service';
import { SearchSuggestionsComponent } from '@shared/components/search-suggestions/search-suggestions.component';
import { HomeSearchCtaBannerInputSuggestionsPipe } from './home-search-cta-banner-input-suggestions.pipe';

@Component({
    selector: 'app-home-search-cta-banner-input',
    imports: [
        CommonModule,
        FormsModule,
        HomeSearchCtaBannerInputSuggestionsPipe,
        MatButtonModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        ReactiveFormsModule,
        SearchSuggestionsComponent,
    ],
    templateUrl: './home-search-cta-banner-input.component.html',
    styleUrls: ['./home-search-cta-banner-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HomeSearchCtaBannerInputComponent implements OnDestroy {
    private el = inject(ElementRef);
    private keplerCoveoService = inject(KeplerCoveoService);
    private keplerSfdcService = inject(KeplerSfdcService);
    private productService = inject(ProductService);
    private recentlyViewedService = inject(RecentlyViewedService);
    private router = inject(Router);
    private userService = inject(UserService);

    /** indicates whether this component is being shown in a mobile viewport */
    isMobile$: Observable<boolean>;
    /** void event that is fired after a search is submitted */
    @Output() afterSearch = new EventEmitter<void>();
    /** form group for user input */
    formGroup = new FormGroup({
        query: new FormControl('', Validators.minLength(1)),
    });
    /** element ref to the {@link searchTerm} form control */
    @ViewChild('searchTermRef') searchTermRef: ElementRef;
    /** css visibility state of the suffix div */
    suffixState: 'hidden' | 'visible' = 'hidden';
    /** search suggestions from Coveo */
    suggestions$: BehaviorSubject<KeplerCoveoSuggestion[]> = new BehaviorSubject([]);
    /** index of the selected suggestion via arrow up/down keys */
    private suggestionIndex$: BehaviorSubject<number> = new BehaviorSubject(-1);
    /** show suggestion container */
    public showSuggestionContainer = false;
    private subs: Subscription[] = [];

    constructor() {
        this.subs.push(
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                this.showSuggestionContainer = false;
                this.clearSuggestions();
            }),
        );
    }

    /**
     * Focus the search input element
     */
    focus(): void {
        this.searchTermRef?.nativeElement?.focus();
    }

    /**
     * Blur the search input element
     */
    blur(): void {
        this.searchTermRef?.nativeElement?.blur();
    }

    /**
     * Emits the active search term text and performs search navigation.
     * @param event - Click event from html template
     */
    handleSearch(event?: Event): void {
        event?.stopPropagation();

        let millis = 0;
        if (this.suggestionIndex$.value > -1) {
            this.selectSuggestion(this.suggestions$.value[this.suggestionIndex$.value]);
            millis = 250; // allow some time for form control to populate for better UX
        }

        // Clear suggestions and hide suggestion container immediately when search is performed
        this.clearSuggestions();
        this.showSuggestionContainer = false;
        this.blur();

        timer(millis) // timer is a one-time subscription and does not need unsubscribed
            .pipe(
                switchMap(() => this.userService.isAuthenticated$),
                switchMap((isAuthenticated) => {
                    return iif(() => isAuthenticated == true, this.keplerSfdcService.supportCases$, of([]));
                }),
                switchMap((cases: SupportCase[]) => {
                    const search = this.formGroup.controls.query.value;
                    console.debug('searching...', search);

                    // if the user searches a case number, open the case in a new tabdd
                    const found = cases?.find((item: SupportCase) => item.case == search);
                    if (found) {
                        window.open(`${environment.mySupport.url}?case=${found.case}`, '_blank');
                        return of(null);
                    } else {
                        return of(search);
                    }
                }),
                combineLatestWith(this.productService.selectedProduct$),
                tap(([search, product]) => {
                    if (search) {
                        // be mindful of order here in this if statement

                        this.handleClear(); // we already have the search term in a variable here, so we can safely clear the user input

                        // emit "after search" [is stored into a variable] to parent components
                        // but before we actually route to the search page.
                        this.afterSearch.emit();
                        this.recentlyViewedService.addSearch(search);

                        this.router.navigate([`/${GknRoutePath.SEARCH}`], {
                            queryParams: { q: search, product: product.uid },
                            queryParamsHandling: 'merge',
                        });
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Emits the selected suggestion as a search term and performs search.
     * @param suggestion - Suggestion to select
     */
    searchSuggestion(suggestion: KeplerCoveoSuggestion): void {
        this.selectSuggestion(suggestion);
        this.clearSuggestions(); // Clear suggestions before performing search
        this.showSuggestionContainer = false; // Hide suggestion container
        this.handleSearch();
    }

    /**
     * Handles all keyup events and fetches search suggestions from Coveo.
     * Shows suggestions panel when input is empty, hides when typing.
     * @param event - Keyboard event
     */
    keyup(event: Event): void {
        event.stopPropagation();

        const queryValue = this.formGroup.controls.query.value;

        if (!queryValue || queryValue.trim() === '') {
            this.clearSuggestions();
            this.showSuggestionContainer = true;
        } else {
            this.showSuggestionContainer = false;
            firstValueFrom(
                this.keplerCoveoService.suggestions$(queryValue).pipe(
                    catchError((error) => {
                        console.error('Error fetching suggestions:', error);
                        return of({ data: [] });
                    }),
                ),
            )
                .then((suggestions: KeplerCoveoSuggestionsResponse) => {
                    const sorted =
                        suggestions.data?.sort((a, b) => a.executableConfidence - b.executableConfidence) || [];
                    this.suggestions$.next(sorted);
                })
                .catch((error) => {
                    console.error('Failed to process suggestions:', error);
                    this.suggestions$.next([]);
                });
        }
    }

    /**
     * Handles key events for the Up arrow key.
     * Increments the suggestionIndex.
     * @param event - Keyboard event
     */
    arrowUp(event: Event): void {
        event.stopPropagation();
        if (this.suggestionIndex$.value == -1) {
            // set the index to the last suggestion
            this.suggestionIndex$.next(this.suggestions$.value.length - 1);
        } else if (this.suggestionIndex$.value > -1) {
            // decrement the index
            this.suggestionIndex$.next(this.suggestionIndex$.value - 1);
        }
    }

    /**
     * Handles key events for the Down arrow key.
     * Decrements the suggestionIndex.
     * @param event - Keyboard event
     */
    arrowDown(event: Event) {
        event.stopPropagation();
        if (this.suggestionIndex$.value == this.suggestions$.value.length - 1) {
            // reset the index
            this.suggestionIndex$.next(-1);
        } else if (this.suggestionIndex$.value >= -1) {
            // increment the index
            this.suggestionIndex$.next(this.suggestionIndex$.value + 1);
        }
    }

    /**
     * Sets the given suggestion as the current search term.
     * Does not submit the search.
     * @param suggestion - Suggestion to set
     */
    selectSuggestion(suggestion: KeplerCoveoSuggestion): void {
        this.formGroup.controls.query.setValue(suggestion.expression);
        this.clearSuggestions();
        this.showSuggestionContainer = false; // Hide suggestion container when suggestion is selected
        this.searchTermRef.nativeElement.focus();
    }

    /**
     * Clears the active search term text and shows suggestion container.
     * @param e - Click event
     */
    handleClear(e?: Event): void {
        e?.stopPropagation();
        this.formGroup.controls.query.setValue('');
        this.clearSuggestions();
        this.showSuggestionContainer = true;
    }

    /**
     * Handle focus event to show suggestions panel
     */
    onFocus(): void {
        this.showSuggestionContainer = true;
    }

    /**
     * Close suggestions panel
     */
    closeSuggestions(): void {
        this.showSuggestionContainer = false;
        this.clearSuggestions();
    }

    /**
     * Clear suggestions list and reset suggestion index
     */
    private clearSuggestions(): void {
        this.suggestionIndex$.next(-1);
        this.suggestions$.next([]);
    }

    /**
     * Handles clicks outside of this component.
     * Clears search suggestions and hides suggestion container.
     * @param event - Click event
     */
    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        event.stopPropagation();
        const target = event.target as HTMLElement;
        if (!target) return;

        const isInsideComponent = this.el.nativeElement.contains(target);
        const isInsideSuggestions = target.closest('.suggestion-container');

        if (!isInsideComponent && !isInsideSuggestions) {
            this.clearSuggestions();
            this.showSuggestionContainer = false;
        }
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     */
    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub.unsubscribe());
    }
}
